/**
 * Weitzman 2026 theme JavaScript
 * Shared utilities: toasts, money/image helpers, focus trap, predictive
 * search and the rest of the non-cart theme behaviour. Cart code is in
 * weitzman-cart.js.
 */
(function () {
  'use strict';

  /* ------------------------------------------------------------------
     Theme routes exposed by layout/theme.liquid
     ------------------------------------------------------------------ */
  const routes = window.Theme && window.Theme.routes ? window.Theme.routes : {};

  /* ------------------------------------------------------------------
     Utility helpers
     ------------------------------------------------------------------ */
  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return Array.from((ctx || document).querySelectorAll(sel)); }

  function fetchJSON(url, options) {
    return fetch(url, Object.assign({ headers: { 'Accept': 'application/json' } }, options || {}))
      .then(function (res) {
        if (!res.ok) { throw new Error('HTTP ' + res.status + ' for ' + url); }
        return res.json();
      });
  }

  /* ------------------------------------------------------------------
     Shared display helpers (money formatting, responsive image URLs).
     Exposed on window.WTZ since this file loads deferred: any inline
     section script that reads window.WTZ.* must do so from an async
     callback (fetch .then, a user-triggered handler) rather than at
     top-level parse time, since a deferred script always runs after
     the page has fully parsed, i.e. after any synchronous inline
     script above it in the DOM has already run.
     ------------------------------------------------------------------ */
  function money(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  function withWidth(url, width) {
    if (!url) return url;
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }

  function srcsetFor(url) {
    return [600, 900, 1200].map(function (w) { return withWidth(url, w) + ' ' + w + 'w'; }).join(', ');
  }

  /* ------------------------------------------------------------------
     Toasts
     ------------------------------------------------------------------
     One place for the transient confirmations the theme used to express by
     swapping a button's own label ("Added", "Could not add. Try again").
     That approach hid the message inside the control the shopper had just
     clicked, which is easy to miss on a long collection page and says
     nothing to a screen reader.

     WTZ.toast(message, { type: 'success' | 'error', duration })

     The live region is created once, on first use, and reused. It is a
     single aria-live container rather than one per toast, because screen
     readers only announce changes inside a region that already existed;
     inserting a new region and its text in the same frame announces
     nothing. 'polite' on success so it waits for a pause, 'assertive' on
     error so it interrupts, matching how urgent each actually is.

     Errors stay twice as long as successes: "Could not add" asks the
     shopper to do something about it, "Added" only confirms what they
     already intended.
  ------------------------------------------------------------------ */
  let toastRegion = null;

  function toastHost() {
    if (toastRegion) return toastRegion;
    toastRegion = document.createElement('div');
    toastRegion.className = 'wtz-toasts';
    toastRegion.setAttribute('role', 'status');
    toastRegion.setAttribute('aria-live', 'polite');
    document.body.appendChild(toastRegion);
    return toastRegion;
  }

  function toast(message, options) {
    if (!message) return;
    const opts = options || {};
    const type = opts.type === 'error' ? 'error' : 'success';
    const host = toastHost();
    /* Assertive only while an error is pending, so a later success does not
       keep interrupting the shopper. */
    host.setAttribute('aria-live', type === 'error' ? 'assertive' : 'polite');

    const el = document.createElement('div');
    el.className = 'wtz-toast wtz-toast--' + type;
    if (opts.html) { el.innerHTML = opts.html; } else { el.textContent = message; }
    host.appendChild(el);

    /* Next frame, so the element is in the DOM with its start styles before
       the class that transitions it in is added. Backed by a timeout
       because requestAnimationFrame does not fire in a background tab: a
       cart add can happen in one, and without this the toast would sit at
       opacity 0 until it was removed again. Adding the class twice is
       harmless. */
    requestAnimationFrame(function () { el.classList.add('is-in'); });
    setTimeout(function () { el.classList.add('is-in'); }, 50);

    const life = opts.duration || (type === 'error' ? 5000 : 2600);
    const hide = function () {
      el.classList.remove('is-in');
      /* Matches the CSS transition; the toast is removed rather than left
         hidden so a long session does not accumulate dead nodes. */
      setTimeout(function () { if (el.parentNode) el.parentNode.removeChild(el); }, 260);
    };
    const timer = setTimeout(hide, life);

    /* Dismiss on click, and stop the timer so hide() cannot run twice. */
    el.addEventListener('click', function () { clearTimeout(timer); hide(); });
    return el;
  }

  window.WTZ = window.WTZ || {};
  window.WTZ.money = money;
  window.WTZ.withWidth = withWidth;
  window.WTZ.srcsetFor = srcsetFor;
  window.WTZ.toast = toast;

  window.WTZ.fetchJSON = fetchJSON;

  /* Keeps Tab/Shift+Tab cycling inside an open dialog instead of leaving it.
     Shared on window.WTZ so every modal in the theme (quickshop,
     promo overlay) uses one implementation instead of a copy each. */
  function trapFocus(container, e) {
    const focusables = container.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])');
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }
  window.WTZ.trapFocus = trapFocus;

  /* Cart, drawer, add to cart and the checkout gate live in
     assets/weitzman-cart.js, loaded straight after this file. */

  /* Recently viewed is owned by weitzman-product.js (writes the handle list
     under wtz_recently_viewed) and weitzman-recently-viewed.js (reads it and
     renders the rail). A second implementation used to live here, keyed on
     weitzman_recently_viewed and storing whole objects, driven by a
     [data-record-recently-viewed] element no template ever rendered. It was
     removed along with a delegated accordion handler whose
     [data-accordion-trigger] markup does not exist either. */

  /* Quick shop is implemented once, in snippets/weitzman-quickshop-modal.liquid
     (fetches /products/{handle}.js and renders variant/price/options client
     side). Collection filter changes are handled once, in
     weitzman-collection.liquid's own inline script (URL rebuild + navigate).
     Neither needs a second implementation here; a previous duplicate of both
     lived in this file and is removed. */

  /* ------------------------------------------------------------------
     Predictive search
     Wires every matching input independently (the header renders a
     desktop AND a mobile search bar on every page, each with its own
     input and results dropdown, plus the /search page's own box), each
     with its own debounce/abort state via closures. */

  /* Ranked by real shopper demand: a live search-query export (Aug 16 -
     Sep 15 2026) was cleaned of autocomplete-keystroke noise (Shopify logs
     every partial-typing state, e.g. "rosh h*"..."rosh hashanah*", as
     separate rows) and clustered by intent; these are the 8 highest-demand
     real terms, each confirmed live to return results via
     /search/suggest.json. Shown before the shopper has typed anything, so
     the box isn't just a blank field -- update this list by hand as demand
     shifts; there's no live feed behind it. Refreshed 2026-09-15: 4 of the
     previous 8 terms (Menorah, Yahrzeit Candle, Ketubah, Hamsa Necklace)
     had fallen to near-zero volume in the newer export while Shofar (158),
     Baseball (156), and Bar/Bat Mitzvah (124+76) -- all missing from the
     old list -- had become the top real queries.

     Refreshed 2026-09-23 against the same search log CROSSED WITH twelve
     months of product sales, because the two signals disagree and demand
     alone was ranking this list badly:
       - "Rosh Hashanah" dropped. It was the single highest-volume query
         (367 with its card variants) but that holiday fell 2026-09-12, so
         the demand is spent and the slot was dead.
       - "Menorah" and "Hanukkah" added despite reading 0 searches in this
         window. The window is Aug 16 to Sep 15, which is BEFORE the season;
         Hanukkah is $72,767 of trailing-twelve-month revenue and begins
         2026-12-04. Both were confirmed to return live results.
       - "Bat Mitzvah" added: 221 searches with its variants, the top real
         query once the spent autumn cluster is set aside.
       - "Star of David" (59) and "Shabbat Candlesticks" (52) added, both
         previously missing despite real demand.
       - "Challah" and "Kiddush Cup" dropped to make room; both are lower
         demand (47 and 29) and lower revenue than what replaced them.
     Every term above was verified live against /search/suggest.json on
     2026-09-23 and returns at least 3 products.

     ponytail: still a hand-maintained list, not a live feed. Ceiling: it
     goes stale as seasons turn, exactly as the Rosh Hashanah slot did.
     Upgrade path: rotate it at each season change from a fresh search
     export, or drive it from a merchant-editable setting. */
  var WTZ_POPULAR_SEARCHES = [
    'Menorah', 'Hanukkah', 'Bat Mitzvah', 'Baseball',
    'Mah Jongg', 'Star of David', 'Mezuzah', 'Shabbat Candlesticks'
  ];

  $$('[data-predictive-search-input]').forEach(function (searchInput) {
    const resultsContainer = $(searchInput.dataset.predictiveSearchResults || '[data-predictive-search-results]');
    if (!resultsContainer) return;
    let debounce;
    let activeSearch;

    /* Always suggests from the whole catalog, regardless of the category
       dropdown -- the dropdown now only affects what happens on submit
       (navigate to the hub, or scope /search by collection:"handle"), not
       the live typeahead. */
    function closeResults() {
      resultsContainer.innerHTML = '';
    }

    function renderPopular(label) {
      resultsContainer.innerHTML = '';
      const heading = document.createElement('p');
      heading.className = 'wtz-search-popular-heading';
      heading.textContent = label || 'Popular searches';
      resultsContainer.appendChild(heading);
      const list = document.createElement('div');
      list.className = 'wtz-search-popular-list';
      WTZ_POPULAR_SEARCHES.forEach(function (term) {
        const a = document.createElement('a');
        a.className = 'wtz-search-popular-term';
        a.href = '/search?q=' + encodeURIComponent(term) + '&type=product';
        a.textContent = term;
        list.appendChild(a);
      });
      resultsContainer.appendChild(list);
    }

    /* Collections are requested alongside products. Someone typing "mezuzah"
       usually wants the aisle, not one item: that query returns 3 products
       but 3 collections, and Mezuzah Cases alone holds 95 products. Showing
       only products hid the better destination. */
    function renderResults(q) {
      activeSearch = new AbortController();
      fetch('/search/suggest.json?q=' + encodeURIComponent(q) + '&resources[type]=product,collection&resources[limit]=6&resources[options][fields]=title,product_type,variants.title,vendor,tag', { signal: activeSearch.signal })
        .then(function (res) { return res.json(); })
        .then(function (json) {
          const results = (json.resources && json.resources.results) || {};
          const products = results.products || [];
          const collections = (results.collections || []).slice(0, 2);

          /* A dead end on a 6,797 product catalogue is the worst outcome in
             the box, so an empty query falls back to the popular terms
             instead of the old bare "No results" line. */
          if (!products.length && !collections.length) {
            renderPopular('No results. Try one of these');
            return;
          }

          resultsContainer.innerHTML = '';

          if (collections.length) {
            const head = document.createElement('p');
            head.className = 'wtz-search-popular-heading';
            head.textContent = 'Collections';
            resultsContainer.appendChild(head);
            collections.forEach(function (c) {
              const a = document.createElement('a');
              a.className = 'wtz-search-result wtz-search-result--collection';
              a.href = c.url;
              const span = document.createElement('span');
              span.textContent = c.title;
              a.appendChild(span);
              resultsContainer.appendChild(a);
            });
          }

          if (products.length) {
            if (collections.length) {
              const ph = document.createElement('p');
              ph.className = 'wtz-search-popular-heading';
              ph.textContent = 'Products';
              resultsContainer.appendChild(ph);
            }
            products.forEach(function (p) {
              const a = document.createElement('a');
              a.className = 'wtz-search-result';
              a.href = p.url;
              if (p.image) {
                const img = document.createElement('img');
                img.src = p.image;
                img.alt = '';
                a.appendChild(img);
              }
              const span = document.createElement('span');
              span.textContent = p.title;
              a.appendChild(span);
              resultsContainer.appendChild(a);
            });
          }
        })
        .catch(function (err) { if (err.name !== 'AbortError') throw err; });
    }

    searchInput.addEventListener('input', function () {
      clearTimeout(debounce);
      if (activeSearch) activeSearch.abort();
      const q = searchInput.value.trim();
      if (!q) { renderPopular(); return; }
      debounce = setTimeout(function () { renderResults(q); }, 250);
    });

    /* Focusing an EMPTY box shows the popular-searches shortcut list
       instead of leaving the dropdown blank. Re-shows cached results on
       refocus (tabbing back into a box that already has a typed term).
       Dismisses on blur or Escape, like Amazon's suggestion dropdown. A
       short blur delay lets a click on a result (or a popular-term)
       register before the dropdown clears out from under it. */
    searchInput.addEventListener('focus', function () {
      const q = searchInput.value.trim();
      if (q && resultsContainer.childElementCount === 0) { renderResults(q); return; }
      if (!q) renderPopular();
    });
    searchInput.addEventListener('blur', function () {
      setTimeout(closeResults, 150);
    });
    searchInput.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { closeResults(); searchInput.blur(); }
    });
  });
})();

/* Mobile card actions: progressive disclosure by tap.
   On touch devices the quick add / quick shop buttons stay hidden until
   the first tap on a card, which reveals them instead of navigating.
   The second tap on the card follows the link; tapping anywhere else
   closes the open card. Desktop hover behavior is untouched. Works for
   server-rendered cards and the recently-viewed section's JS-built
   clones alike, since the listener is delegated at the document level. */
(function () {
  if (!window.matchMedia || !window.matchMedia('(hover: none)').matches) return;
  document.addEventListener('click', function (e) {
    if (e.target.closest('.wtz-card__actions')) return;
    const card = e.target.closest('.wtz-card--grid');
    const open = document.querySelector('.wtz-card--grid.is-touch-open');
    /* The first tap used to swallow the navigation to reveal quick-add, so
       every mobile PDP cost two taps. That is a funnel tax on the primary
       path (tap product -> read PDP) to expose a secondary shortcut, and it
       tracks the measured add-to-cart drop. Now a tap on the product link
       always navigates on the first try; the actions still reveal for taps
       on the non-link parts of the card. */
    if (card && !card.classList.contains('is-touch-open')) {
      if (open && open !== card) open.classList.remove('is-touch-open');
      card.classList.add('is-touch-open');
      return;
    }
    if (!card && open) open.classList.remove('is-touch-open');
  });
})();
